/**
 * cm-segmented-banner.js — hero segmented banner for
 * sections/segmented-banner.liquid (SEC-15, build-spec 04d-sections-home-a).
 *
 * Custom element <cm-segmented-banner>: wipes between [data-cm-segment]
 * panels and drives the [data-cm-tab] strip (aria tablist). Autoplay comes
 * from data-autoplay (ms, "0" disables), pause-on-hover from
 * data-pause-hover. The wipe duration is read from the CSS variable
 * --cm-hero-duration (1000 ms mobile / setting desktop, set in
 * section-home-hero.css).
 *
 * With prefers-reduced-motion (or CM.config.motion === false) segments
 * switch instantly: no autoplay, no is-entering/is-leaving classes.
 */
'use strict';

(function () {
  class CMSegmentedBanner extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      this._segments = Array.prototype.slice.call(this.querySelectorAll('[data-cm-segment]'));

      // PERFORMANCE (QC-OPUS-R1 30): segments after the first ship their image
      // URLs in data-cm-* attributes (snippets/image.liquid defer_load) so they
      // never fetch inside the LCP window. Hydrate once the window has loaded
      // and the thread is idle; goTo() hydrates synchronously as a fallback.
      this._hydrate = () => {
        if (window.CM && typeof window.CM.hydrateImages === 'function') window.CM.hydrateImages(this);
      };
      if (window.CM && typeof window.CM.afterLoad === 'function') window.CM.afterLoad(this._hydrate);
      else this._hydrate();
      this._tabs = Array.prototype.slice.call(this.querySelectorAll('[data-cm-tab]'));
      this._current = 0;
      this._locked = false;
      this._paused = false;
      this._inView = true;
      this._timer = null;
      this._lockTimer = null;

      this._autoplayMs = parseInt(this.dataset.autoplay, 10) || 0;
      this._pauseOnHover = this.dataset.pauseHover === 'true';

      const reduced = this._reduced();
      if (reduced || this._segments.length < 2) this._autoplayMs = 0;

      this._onPointerEnter = () => {
        if (this._pauseOnHover) this._pause();
      };
      this._onPointerLeave = () => {
        if (!this.matches(':focus-within')) this._resume();
      };
      this._onFocusIn = (event) => {
        if (!this.contains(event.relatedTarget)) this._pause();
      };
      this._onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget) && !(this._pauseOnHover && this.matches(':hover'))) this._resume();
      };
      this._onVisibility = () => {
        if (document.hidden) this._pause();
        else if (!(this._pauseOnHover && this.matches(':hover'))) this._resume();
      };
      this._onTabClick = (event) => {
        const index = this._tabs.indexOf(event.currentTarget);
        if (index === -1) return;
        this._activate(index);
      };
      this._onTabKeydown = (event) => this._onKeydown(event);

      this._bind();

      // Jlood plays the hero text reveal on load (info overlay clip/opacity,
      // delay .5 s mobile / .8 s desktop — codemap hero-sections.md §1). The
      // first segment ships with SSR is-active, so replay it once the element
      // upgrades: remove, reflow, re-add on the next frames -> the transition
      // actually runs. No IntersectionObserver: the hero is above the fold.
      if (!reduced && this._segments.length > 0) {
        this._prepWords();
        const first = this._segments[0];
        first.classList.remove('is-active');
        void this.offsetWidth;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            first.classList.add('is-active');
            this._revealWords(first);
          });
        });
      }

      if (this._autoplayMs > 0) {
        if (typeof IntersectionObserver === 'function') {
          this._observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => this._onViewChange(entry.isIntersecting));
          }, { threshold: 0.2 });
          this._observer.observe(this);
        } else {
          this._startFill();
          this._schedule();
        }
      }
    }

    disconnectedCallback() {
      this._unbind();
      clearTimeout(this._timer);
      clearTimeout(this._lockTimer);
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      delete this.dataset.ready;
    }

    _reduced() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        (window.CM && window.CM.config && window.CM.config.motion === false);
    }

    /* --cm-hero-duration from the stylesheet, "1600ms"/"1.6s" -> ms */
    _duration() {
      const raw = getComputedStyle(this).getPropertyValue('--cm-hero-duration').trim();
      const match = raw.match(/^([\d.]+)(ms|s)$/);
      if (!match) return 1000;
      const value = parseFloat(match[1]);
      return match[2] === 's' ? value * 1000 : value;
    }

    /* Per-word stagger reveal (Jlood anm-text-overlay-slide: every headline
       word is an inline-block span sliding out of a clip, 490ms ease-in-out,
       word i starting at 0.5 x duration + i x 120ms; the 0.5 factor and
       490ms are captured, the 120ms step is our interpolation). Words are
       pre-hidden inline so no-JS and reduced-motion users always see text. */
    _prepWords() {
      this._segments.forEach((segment) => {
        const headline = segment.querySelector('[data-cm-hero-words]');
        if (!headline) return;
        const spans = Array.prototype.slice.call(headline.querySelectorAll('.cm-hero__word'));
        if (spans.length < 2) return;
        this._ensureWordSpaces(headline, spans);
        segment._cmWords = spans;
        this._hideWords(segment);
      });
    }

    /* A real SPACE text node between the word spans: textContent must equal
       the original headline (spaces included) for screen readers and
       copy/paste, and without it adjacent inline-block spans render glued
       together ("أطقمدافية"). Liquid emits the spaces; this repairs any
       markup that lost them. */
    _ensureWordSpaces(headline, spans) {
      for (let i = 0; i < spans.length - 1; i++) {
        const node = spans[i].nextSibling;
        if (node && node.nodeType === 3) {
          if (/^\s+$/.test(node.nodeValue)) node.nodeValue = ' ';
          continue;
        }
        if (node === spans[i + 1]) {
          headline.insertBefore(document.createTextNode(' '), spans[i + 1]);
        }
      }
    }

    _revealWords(segment) {
      const spans = segment && segment._cmWords;
      if (!spans) return;
      const base = this._duration() * 0.5;
      spans.forEach((span, i) => {
        const delay = Math.round(base + i * 120);
        span.style.transition = 'opacity 490ms ease-in-out ' + delay + 'ms, clip-path 490ms ease-in-out ' + delay + 'ms';
        span.style.opacity = '1';
        span.style.clipPath = 'inset(0 0 0 0)';
      });
    }

    _hideWords(segment) {
      const spans = segment && segment._cmWords;
      if (!spans) return;
      const rtl = document.documentElement.dir === 'rtl';
      spans.forEach((span) => {
        span.style.transition = 'none';
        span.style.opacity = '0';
        span.style.clipPath = rtl ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)';
      });
    }

    _bind() {
      this.addEventListener('pointerenter', this._onPointerEnter);
      this.addEventListener('pointerleave', this._onPointerLeave);
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);
      document.addEventListener('visibilitychange', this._onVisibility);
      this._tabs.forEach((tab) => {
        tab.addEventListener('click', this._onTabClick);
        tab.addEventListener('keydown', this._onTabKeydown);
      });
    }

    _unbind() {
      this.removeEventListener('pointerenter', this._onPointerEnter);
      this.removeEventListener('pointerleave', this._onPointerLeave);
      this.removeEventListener('focusin', this._onFocusIn);
      this.removeEventListener('focusout', this._onFocusOut);
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._tabs.forEach((tab) => {
        tab.removeEventListener('click', this._onTabClick);
        tab.removeEventListener('keydown', this._onTabKeydown);
      });
    }

    /* returns true only when the slide actually changed */
    goTo(index, user) {
      const total = this._segments.length;
      if (total < 1) return false;
      const target = ((index % total) + total) % total;
      if (target === this._current || this._locked) return false;

      const previous = this._segments[this._current];
      const next = this._segments[target];
      if (this._hydrate) this._hydrate();
      this._current = target;

      this._updateTabs(target);

      if (this._reduced()) {
        // instant swap: no wipe classes, no timeouts
        previous.classList.remove('is-active');
        next.classList.add('is-active');
        if (user) this._focusSegment(next);
        return true;
      }

      this._locked = true;
      previous.classList.remove('is-active');
      previous.classList.add('is-leaving');
      next.classList.add('is-active', 'is-entering');
      this._hideWords(previous);
      this._revealWords(next);

      clearTimeout(this._lockTimer);
      this._lockTimer = setTimeout(() => {
        previous.classList.remove('is-leaving');
        next.classList.remove('is-entering');
        this._locked = false;
        // Jlood updateFocsingElement: the entering slide takes focus once the
        // wipe settles — user-initiated switches only, autoplay never steals
        // focus (codemap hero-sections.md §1 gap list).
        if (user) this._focusSegment(next);
      }, this._duration());
      return true;
    }

    _focusSegment(segment) {
      if (!segment) return;
      const heading = segment.querySelector('.cm-hero__headline');
      if (heading) heading.focus({ preventScroll: true });
    }

    _updateTabs(index) {
      const running = this._autoplayMs > 0 && !this._paused;
      this._tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('is-active', active);
        tab.classList.remove('is-running');
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      if (running) {
        void this.offsetWidth; // force reflow so the fill restarts from zero
        this._tabs[index].classList.add('is-running');
      }
    }

    /* tab click / keyboard activation: switch + restart the autoplay timer */
    _activate(index, keepFocus) {
      // keepFocus (arrow/Home/End): focus stays on the tab so the next arrow press keeps working
      // (WAI-ARIA tabs). Click/Enter/Space keep Jlood's move-focus-to-slide-heading behaviour.
      if (!this.goTo(index, !keepFocus)) return;
      clearTimeout(this._timer);
      this._startFill();
      this._schedule();
    }

    _startFill() {
      if (this._autoplayMs <= 0 || this._paused) return;
      const tab = this._tabs[this._current];
      if (!tab) return;
      tab.classList.remove('is-running');
      void this.offsetWidth;
      tab.classList.add('is-running');
    }

    _schedule() {
      clearTimeout(this._timer);
      if (this._autoplayMs <= 0 || this._paused || !this._inView) return;
      this._timer = setTimeout(() => {
        this.goTo(this._current + 1, false);
        this._startFill();
        this._schedule();
      }, this._autoplayMs);
    }

    _pause() {
      if (this._paused) return;
      this._paused = true;
      this.classList.add('is-paused');
      clearTimeout(this._timer);
    }

    /* fresh full interval: the running fill restarts from zero */
    _resume() {
      if (!this._paused) return;
      this._paused = false;
      this.classList.remove('is-paused');
      this._startFill();
      this._schedule();
    }

    _onViewChange(inView) {
      this._inView = inView;
      if (!inView) {
        clearTimeout(this._timer);
        const tab = this._tabs[this._current];
        if (tab) tab.classList.remove('is-running');
      } else if (this._autoplayMs > 0 && !this._paused) {
        this._startFill();
        this._schedule();
      }
    }

    _onKeydown(event) {
      const total = this._tabs.length;
      if (total < 1) return;
      const rtl = document.documentElement.dir === 'rtl';
      let index = this._tabs.indexOf(event.currentTarget);
      switch (event.key) {
        case 'ArrowRight': index += rtl ? -1 : 1; break;
        case 'ArrowLeft': index += rtl ? 1 : -1; break;
        case 'ArrowDown': index += 1; break;
        case 'ArrowUp': index -= 1; break;
        case 'Home': index = 0; break;
        case 'End': index = total - 1; break;
        default: return;
      }
      event.preventDefault();
      index = ((index % total) + total) % total;
      this._tabs[index].focus();
      this._activate(index, true);
    }
  }

  if (!customElements.get('cm-segmented-banner')) {
    customElements.define('cm-segmented-banner', CMSegmentedBanner);
  }
})();
